import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import type { Label as LabelType, User } from '@shared/types';
import { cn } from '@/lib/format';
import { Avatar, LabelChip } from './ui';

/**
 * Inline editors for the task detail pane.
 *
 * All follow the same contract: they render a display value that becomes an
 * editing control on click, and call `onSave` with the new value. Saving is the
 * caller's job (an optimistic mutation), so these stay presentational and
 * reusable across fields.
 */

/* ---------- editable single-line text (title) ---------- */

export function EditableText({
  value,
  onSave,
  className,
  placeholder = 'Titel…',
  multiline = false,
}: {
  value: string;
  onSave: (v: string) => void;
  className?: string;
  placeholder?: string;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement | HTMLInputElement>(null);

  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);
  // Keep the draft in sync if the underlying value changes while not editing.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    else setDraft(value); // reject empty; revert
  }

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className={cn('w-full rounded px-1 -mx-1 text-left hover:bg-[var(--surface-hover)]', className)}
      >
        {value || <span className="text-faint">{placeholder}</span>}
      </button>
    );
  }

  const shared = {
    ref: ref as never,
    value: draft,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
    onBlur: commit,
    className: cn(
      'w-full rounded border border-brand-400 surface-card px-1 -mx-1 focus:outline-none focus:ring-1 focus:ring-brand-500',
      className,
    ),
  };

  return multiline ? (
    <textarea
      {...shared}
      rows={3}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit();
        if (e.key === 'Escape') { setDraft(value); setEditing(false); }
      }}
    />
  ) : (
    <input
      {...shared}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { setDraft(value); setEditing(false); }
      }}
    />
  );
}

/* ---------- date field ---------- */

export function EditableDate({
  value,
  onSave,
  label,
}: {
  value: string | null;
  onSave: (iso: string | null) => void;
  label: string;
}) {
  // <input type=date> wants yyyy-MM-dd; the stored value is a full ISO string.
  const dateOnly = value ? value.slice(0, 10) : '';
  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={dateOnly}
        onChange={(e) => {
          const v = e.target.value;
          // Preserve time-of-day semantics: store midnight UTC for a date-only
          // pick, or clear to null when emptied.
          onSave(v ? new Date(`${v}T00:00:00.000Z`).toISOString() : null);
        }}
        aria-label={label}
        className="rounded border line surface-card px-2 py-1 text-xs text-strong
                   focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      {value && (
        <button
          onClick={() => onSave(null)}
          className="text-faint hover:text-red-500"
          aria-label={`${label} löschen`}
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/* ---------- priority select ---------- */

const PRIORITIES = [
  { v: 0, label: 'Keine' },
  { v: 1, label: 'Niedrig' },
  { v: 2, label: 'Mittel' },
  { v: 3, label: 'Hoch' },
  { v: 4, label: 'Dringend' },
  { v: 5, label: 'JETZT' },
];

export function EditablePriority({ value, onSave }: { value: number; onSave: (v: number) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onSave(Number(e.target.value))}
      className="rounded border line surface-card px-2 py-1 text-xs text-strong
                 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      aria-label="Priorität"
    >
      {PRIORITIES.map((p) => (
        <option key={p.v} value={p.v}>
          {p.label}
        </option>
      ))}
    </select>
  );
}

/* ---------- multi-select picker (assignees, labels) ---------- */

/**
 * A dropdown multi-select used for both assignees and labels. Generic over the
 * option type so the two callers share the open/close, keyboard, and
 * click-outside behaviour rather than duplicating it.
 */
export function MultiSelect<T extends { id: number }>({
  options,
  selectedIds,
  onChange,
  renderOption,
  renderChip,
  placeholder,
  searchText,
}: {
  options: T[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  renderOption: (o: T, selected: boolean) => React.ReactNode;
  renderChip: (o: T) => React.ReactNode;
  placeholder: string;
  searchText: (o: T) => string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const selected = new Set(selectedIds);
  const filtered = query
    ? options.filter((o) => searchText(o).toLowerCase().includes(query.toLowerCase()))
    : options;

  function toggle(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  }

  const chosen = options.filter((o) => selected.has(o.id));

  return (
    <div ref={boxRef} className="relative">
      <div className="flex flex-wrap items-center gap-1.5">
        {chosen.map((o) => (
          <span key={o.id}>{renderChip(o)}</span>
        ))}
        <button
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 rounded-full border border-dashed line px-2 py-0.5
                     text-[11px] text-muted hover:border-brand-400 hover:text-brand-500"
        >
          <Plus className="size-3" />
          {chosen.length === 0 ? placeholder : 'Ändern'}
          <ChevronDown className="size-3" />
        </button>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-56 overflow-y-auto rounded-lg surface-card border line py-1 shadow-lg scroll-thin">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suchen…"
            autoFocus
            className="mx-2 mb-1 w-[calc(100%-1rem)] rounded border line surface-sunken px-2 py-1 text-xs
                       focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-faint">Keine Treffer</p>
          )}
          {filtered.map((o) => {
            const isSel = selected.has(o.id);
            return (
              <button
                key={o.id}
                onClick={() => toggle(o.id)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-[var(--surface-hover)]"
              >
                <span className={cn('grid size-4 shrink-0 place-items-center rounded border', isSel ? 'border-brand-500 bg-brand-500 text-white' : 'line')}>
                  {isSel && <Check className="size-3" />}
                </span>
                {renderOption(o, isSel)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AssigneePicker({
  users,
  selectedIds,
  onChange,
}: {
  users: User[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  return (
    <MultiSelect
      options={users}
      selectedIds={selectedIds}
      onChange={onChange}
      placeholder="Zuweisen"
      searchText={(u) => u.name}
      renderChip={(u) => (
        <span className="inline-flex items-center gap-1 rounded-full surface-sunken py-0.5 pl-0.5 pr-2 text-[11px]">
          <Avatar user={u} size="xs" />
          {u.name}
        </span>
      )}
      renderOption={(u) => (
        <span className="flex items-center gap-2">
          <Avatar user={u} size="xs" />
          {u.name}
        </span>
      )}
    />
  );
}

export function LabelPicker({
  labels,
  selectedIds,
  onChange,
}: {
  labels: LabelType[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  return (
    <MultiSelect
      options={labels}
      selectedIds={selectedIds}
      onChange={onChange}
      placeholder="Label"
      searchText={(l) => l.title}
      renderChip={(l) => <LabelChip label={l} />}
      renderOption={(l) => <LabelChip label={l} />}
    />
  );
}
